import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import {Avatar, Divider, Grid, Typography} from '@mui/material';

import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {greenColors, orangeColors} from '../../../../../../layouts/theme/designSystem';
import {MatchData} from '../../../../../../types/shared-recruiting-types';
import {copyTextToClipBoard} from '../../../../../../utils/copyTextToClipboard/copyTextToClipBoard';

interface Props {
  match: MatchData;
  /** Omit campaign and matched date (talent pool profile without a match). */
  variant?: 'match' | 'talentPool';
}

export const HeaderInformation = ({match, variant = 'match'}: Props) => {
  const {dispatchMessage} = useContext(SnackBarContext);
  const applicant = match.applicant;
  const {t: tr} = useTranslation('translation', {keyPrefix: 'applicantDashboard.matchesView'});
  const {t: tb} = useTranslation('translation', {keyPrefix: 'companyApplicants.snackbars'});
  const {t: tCommon} = useTranslation('translation');
  const copyToClipboard = (e: React.MouseEvent, field: 'email' | 'phone') => {
    e.stopPropagation();

    const applicantEmail = applicant?.personalInformation.email;
    const applicantPhone = applicant?.personalInformation.mobilePhoneNumber;

    if (field === 'email' && applicantEmail) {
      copyTextToClipBoard(applicantEmail);
      dispatchMessage(tb('copyEmail'), 'success');
    } else if (field === 'phone' && applicantPhone) {
      copyTextToClipBoard(applicantPhone);
      dispatchMessage(tb('copyPhone'), 'success');
    }
  };

  const phone = applicant?.personalInformation.mobilePhoneNumber;
  const email = applicant?.personalInformation.email;
  const avatarUrl = applicant?.profilePictureUrl?.trim() || applicant?.cultureType?.imageUrl?.trim() || '';

  return (
    <Grid item container justifyContent={'space-between'} alignItems={'flex-start'}>
      <Grid>
        <Grid item display={'flex'} alignItems={'center'}>
          <Grid item mr={1}>
            <Avatar
              src={avatarUrl || undefined}
              alt={`${applicant?.personalInformation.firstName || ''} ${
                applicant?.personalInformation.lastName || ''
              }`.trim()}
              sx={{width: 44, height: 44, border: `2px solid ${greenColors[5]}`}}
            />
          </Grid>
          <Grid item mr={1}>
            <Typography variant='h1'>{applicant?.personalInformation.firstName}</Typography>
          </Grid>
          <Grid item>
            <Typography variant='h1' mr={1}>
              {applicant?.personalInformation.lastName}
            </Typography>
          </Grid>
        </Grid>
        <Grid item mt={1}>
          <Grid item display='flex' alignItems='center' flexWrap='wrap' columnGap={1}>
            {phone ? (
              <Grid
                item
                display='flex'
                alignItems='center'
                onClick={(e) => copyToClipboard(e, 'phone')}
                sx={{cursor: 'pointer'}}
              >
                <PhoneOutlinedIcon sx={{fontSize: 14, color: greenColors[700], mr: 0.5}} />
                <Typography variant='body2'>{phone}</Typography>
              </Grid>
            ) : null}
            {phone && email ? (
              <Typography variant='body2' sx={{color: greenColors[100]}}>
                |
              </Typography>
            ) : null}
            {email ? (
              <Grid
                item
                display='flex'
                alignItems='center'
                onClick={(e) => copyToClipboard(e, 'email')}
                sx={{cursor: 'pointer'}}
              >
                <EmailOutlinedIcon sx={{fontSize: 14, color: greenColors[700], mr: 0.5}} />
                <Typography variant='body2'>{email}</Typography>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
      {variant === 'match' ? (
        <Grid sx={{textAlign: 'right', mt: 0.5}}>
          <Typography variant='body1'>
            {tCommon('talentPoolProfilePage.applicationCard.campaign')}: {match.campaignName || '—'}
          </Typography>
          <Typography variant='body2' sx={{color: greenColors[100]}}>
            {tr('matched')}: {match.createdAt}
          </Typography>
        </Grid>
      ) : null}
      <Divider sx={{width: '100%', my: 2, color: orangeColors[3]}} />
    </Grid>
  );
};
